import { useState, useEffect } from "react";
import { apiClient, useAuth } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useConfirmation } from "@/components/ConfirmationProvider";
import { BookOpen, Plus, Link2, Trash2, Globe, Calendar, Tag, X, Loader2, ArrowLeft, ExternalLink, Save, NotebookPen, Sparkles, Lock } from "lucide-react";
import { format, parseISO } from "date-fns";
import { Link as RouterLink } from "react-router-dom";

const TagChip = ({ value, onRemove }) => (
  <Badge variant="secondary" className="rounded-full text-xs gap-1.5 pl-2.5 pr-1">
    {value}
    {onRemove && (
      <button type="button" onClick={onRemove} className="hover:bg-destructive/20 rounded-full p-0.5">
        <X className="h-3 w-3" />
      </button>
    )}
  </Badge>
);

function TagInput({ tags, setTags, placeholder = "Add tag, press Enter" }) {
  const [val, setVal] = useState("");
  const add = (raw) => {
    const v = raw.trim().toLowerCase();
    if (v && !tags.includes(v)) setTags([...tags, v]);
    setVal("");
  };
  return (
    <div className="flex flex-wrap gap-1.5 p-2 rounded-xl border bg-background">
      {tags.map(t => (<TagChip key={t} value={t} onRemove={() => setTags(tags.filter(x => x !== t))} />))}
      <input
        value={val}
        onChange={e => setVal(e.target.value)}
        onKeyDown={e => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(val); } }}
        onBlur={() => val && add(val)}
        placeholder={tags.length ? "" : placeholder}
        className="flex-1 min-w-[120px] bg-transparent outline-none text-sm py-1 px-1"
        data-testid="tag-input"
      />
    </div>
  );
}

export default function MycyclopediaPage() {
  const { user } = useAuth();
  const confirm = useConfirmation();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [tags, setTags] = useState([]);
  const [saving, setSaving] = useState(false);
  const [filterTag, setFilterTag] = useState("");
  const [reading, setReading] = useState(null);
  const [readingTags, setReadingTags] = useState([]);
  const [readingNotes, setReadingNotes] = useState("");
  const [summarizing, setSummarizing] = useState(false);
  const [aiDisabled, setAiDisabled] = useState(false);

  const isPremium = user?.subscription_tier === "premium";

  useEffect(() => {
    fetchArticles();
    apiClient.get("/settings/ai")
      .then((r) => setAiDisabled(!!r.data?.ai_disabled))
      .catch(() => setAiDisabled(false));
  }, [filterTag]);

  const fetchArticles = async () => {
    try {
      const r = await apiClient.get("/mycyclopedia" + (filterTag ? `?tag=${encodeURIComponent(filterTag)}` : ""));
      setArticles(r.data);
    } catch { toast.error("Failed to load library"); }
    setLoading(false);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!isPremium) {
      toast.error("Library is a Premium feature — upgrade to save articles");
      return;
    }
    setSaving(true);
    try {
      await apiClient.post("/mycyclopedia", { url, tags });
      toast.success("Article saved");
      setUrl(""); setTags([]); setDialogOpen(false);
      fetchArticles();
    } catch (err) {
      const code = err.response?.data?.code;
      if (code === "tier_premium_required") {
        toast.error("Library is a Premium feature — upgrade to save articles");
      } else {
        toast.error(err.response?.data?.detail || "Could not save");
      }
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!(await confirm({ title: "Remove article?", description: "This saved article will be permanently removed from your library.", confirmLabel: "Remove" }))) return;
    try {
      await apiClient.delete(`/mycyclopedia/${id}`);
      toast.success("Removed");
      fetchArticles();
      if (reading?.id === id) setReading(null);
    } catch { toast.error("Failed to remove"); }
  };

  const openArticle = async (a) => {
    try {
      const r = await apiClient.get(`/mycyclopedia/${a.id}`);
      setReading(r.data);
      setReadingTags(r.data.tags || []);
      setReadingNotes(r.data.notes || "");
    } catch { toast.error("Could not open article"); }
  };

  const saveReadingMeta = async () => {
    if (!reading) return;
    try {
      const r = await apiClient.patch(`/mycyclopedia/${reading.id}`, { tags: readingTags, notes: readingNotes });
      setReading(r.data);
      toast.success("Saved");
      fetchArticles();
    } catch { toast.error("Could not save"); }
  };

  const handleSummarize = async () => {
    if (!reading) return;
    setSummarizing(true);
    try {
      const r = await apiClient.post(`/mycyclopedia/${reading.id}/summarize`);
      setReading(prev => ({ ...prev, ai_summary: r.data }));
      toast.success("AI summary generated");
    } catch (err) {
      const detail = err.response?.data?.detail || "Summary failed";
      const code = err.response?.data?.code;
      if (code === "tier_premium_required") {
        toast.error("Upgrade to Premium to use AI features");
      } else if (detail.includes("AI not configured")) {
        toast.error("Add your AI key in Settings → AI (BYOK) first", { duration: 8000 });
      } else {
        toast.error(detail);
      }
    }
    setSummarizing(false);
  };

  // Reading view
  if (reading) {
    return (
      <div className="space-y-6 max-w-4xl mx-auto" data-testid="article-reader">
        <Button variant="ghost" onClick={() => setReading(null)} className="rounded-full" data-testid="back-to-library">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Library
        </Button>

        <Card className="border-0 shadow-lg rounded-3xl">
          <CardContent className="p-6 md:p-8">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div className="min-w-0">
                <h1 className="font-heading text-2xl md:text-3xl font-bold mb-1" data-testid="article-title">{reading.title}</h1>
                <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  {reading.byline && <span>{reading.byline}</span>}
                  {reading.site_name && <span className="flex items-center gap-1"><Globe className="h-3 w-3" /> {reading.site_name}</span>}
                  <a href={reading.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-primary hover:underline">
                    Original <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => handleDelete(reading.id)} className="rounded-full text-destructive">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>

            <div className="mb-6 space-y-3">
              <div>
                <Label className="text-xs font-semibold">Tags</Label>
                <TagInput tags={readingTags} setTags={setReadingTags} />
              </div>
              <div>
                <Label className="text-xs font-semibold flex items-center gap-1.5"><NotebookPen className="h-3.5 w-3.5" /> My notes</Label>
                <Textarea value={readingNotes} onChange={e => setReadingNotes(e.target.value)} placeholder="What stood out? What did you want to try?" rows={3} className="rounded-xl resize-none" data-testid="article-notes-input" />
              </div>
              <Button onClick={saveReadingMeta} className="rounded-full" data-testid="save-article-meta-btn">
                <Save className="mr-2 h-4 w-4" /> Save notes & tags
              </Button>
            </div>

            {/* AI summary panel */}
            {!aiDisabled && <div className="mb-6 p-4 rounded-2xl bg-primary/5 border border-primary/15" data-testid="ai-summary-panel">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-primary" />
                  <h3 className="font-heading font-bold text-sm">AI Summary</h3>
                  <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-primary/10 text-primary">Premium</span>
                </div>
                {isPremium ? (
                  <Button
                    size="sm"
                    onClick={handleSummarize}
                    disabled={summarizing}
                    className="rounded-full"
                    data-testid="summarize-article-btn"
                  >
                    {summarizing ? (
                      <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Thinking…</>
                    ) : reading.ai_summary ? (
                      <><Sparkles className="mr-1.5 h-3.5 w-3.5" /> Regenerate</>
                    ) : (
                      <><Sparkles className="mr-1.5 h-3.5 w-3.5" /> Generate</>
                    )}
                  </Button>
                ) : (
                  <RouterLink to="/#pricing" className="text-xs text-primary font-medium hover:underline flex items-center gap-1">
                    <Lock className="h-3 w-3" /> Upgrade
                  </RouterLink>
                )}
              </div>
              {reading.ai_summary ? (
                <div data-testid="ai-summary-content">
                  {reading.ai_summary.tldr && (
                    <p className="text-sm font-medium mb-3 leading-relaxed">{reading.ai_summary.tldr}</p>
                  )}
                  {Array.isArray(reading.ai_summary.key_points) && reading.ai_summary.key_points.length > 0 && (
                    <ul className="space-y-1.5 text-sm">
                      {reading.ai_summary.key_points.map((p, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="text-primary flex-shrink-0 mt-1">•</span>
                          <span className="leading-relaxed">{p}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {reading.ai_summary.generated_at && (
                    <p className="text-[11px] text-muted-foreground mt-3">
                      Generated by {reading.ai_summary.provider} · {reading.ai_summary.model}
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground italic">
                  {isPremium ? "Generate a quick TL;DR + key points using your own AI key. Set it up in Settings → AI." : "Premium feature — upgrade to summarize articles in one click."}
                </p>
              )}
            </div>}

            {reading.content_html ? (
              <article
                className="article-prose"
                dangerouslySetInnerHTML={{ __html: reading.content_html }}
                data-testid="article-content"
              />
            ) : (
              <p className="text-muted-foreground italic">Could not extract readable content. Open the original link above.</p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return <div className="space-y-6 animate-pulse"><div className="h-10 bg-muted rounded-lg w-48" /><div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">{[1,2,3,4,5,6].map(i => <div key={i} className="h-44 bg-muted rounded-3xl" />)}</div></div>;
  }

  return (
    <div className="space-y-6" data-testid="mycyclopedia-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-primary flex items-center gap-3">
            <BookOpen className="h-8 w-8" />
            Library
            {!isPremium && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-primary/10 text-primary">Premium</span>}
          </h1>
          <p className="text-muted-foreground mt-1">
            {isPremium
              ? "Your personal collection of articles, saved for offline reading"
              : "Save articles from the web and community — upgrade to Premium to unlock"}
          </p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full" data-testid="save-article-btn">
              <Plus className="mr-2 h-4 w-4" /> Save Article
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-3xl sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading">Save an Article</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSave} className="space-y-4 mt-3">
              <div className="space-y-2">
                <Label>URL</Label>
                <Input type="url" required value={url} onChange={e => setUrl(e.target.value)} placeholder="https://example.com/article" className="rounded-xl" data-testid="article-url-input" />
              </div>
              <div className="space-y-2">
                <Label>Tags (optional)</Label>
                <TagInput tags={tags} setTags={setTags} />
              </div>
              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1 rounded-full">Cancel</Button>
                <Button type="submit" disabled={saving} className="flex-1 rounded-full" data-testid="confirm-save-article-btn">
                  {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Extracting…</> : <><Link2 className="mr-2 h-4 w-4" /> Save</>}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {filterTag && (
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Filtered by tag:</span>
          <TagChip value={filterTag} onRemove={() => setFilterTag("")} />
        </div>
      )}

      {articles.length === 0 ? (
        <Card className="border-0 shadow-lg rounded-3xl"><CardContent className="py-16 text-center">
          <BookOpen className="h-16 w-16 mx-auto text-muted-foreground/30 mb-4" />
            <h3 className="font-heading text-xl font-bold mb-2">{filterTag ? "No articles with this tag" : "Your library is empty"}</h3>
          <p className="text-muted-foreground mb-6">Save articles you want to come back to. Notes & tags supported.</p>
          <Button onClick={() => setDialogOpen(true)} className="rounded-full"><Plus className="mr-2 h-4 w-4" /> Save your first article</Button>
        </CardContent></Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {articles.map(a => (
            <Card
              key={a.id}
              className="border-0 shadow-lg rounded-3xl card-hover cursor-pointer transition-transform hover:-translate-y-0.5"
              onClick={() => openArticle(a)}
              data-testid={`article-card-${a.id}`}
            >
              <CardContent className="p-5 space-y-3">
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <BookOpen className="h-5 w-5 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-heading font-bold text-base line-clamp-2 mb-0.5">{a.title}</h3>
                    {a.site_name && <p className="text-xs text-muted-foreground flex items-center gap-1"><Globe className="h-3 w-3" /> {a.site_name}</p>}
                  </div>
                </div>
                {a.excerpt && <p className="text-sm text-muted-foreground line-clamp-3">{a.excerpt}</p>}
                {a.tags && a.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {a.tags.slice(0, 4).map(t => (
                      <button key={t} onClick={(e) => { e.stopPropagation(); setFilterTag(t); }} className="hover:opacity-80" data-testid={`tag-${t}`}>
                        <TagChip value={t} />
                      </button>
                    ))}
                  </div>
                )}
                <div className="flex items-center justify-between text-xs text-muted-foreground pt-1 border-t border-border">
                  <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {format(parseISO(a.saved_at), "MMM d, yyyy")}</span>
                  <button onClick={(e) => { e.stopPropagation(); handleDelete(a.id); }} className="hover:text-destructive transition-colors" data-testid={`delete-article-${a.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
